import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AppLayout from '@/components/layout/AppLayout';
import LoginPage from '@/features/auth/LoginPage';
import LiveMapPage from '@/features/map/LiveMapPage';
import useFeatures from '@/hooks/useFeatures';
import useColdChainReportAccess from '@/features/cold-chain/useColdChainReportAccess';
import { useInstanceAccess } from '@/hooks/useInstanceAccess';
// isSupabaseConfigured removed — Trackizy Server is always available

// Lazy-loaded pages (not needed on initial load). The Dashboard is lazy too:
// its widget renderers pull in Recharts, which login and the Live Map never need.
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const ReplayPage = lazy(() => import('@/features/replay/ReplayPage'));
const HelpPage = lazy(() => import('@/features/help/HelpPage'));
const SupportPage = lazy(() => import('@/features/support/SupportPage'));
const SharedTrackingPage = lazy(() => import('@/features/sharing/SharedTrackingPage'));
const BusLinkPage = lazy(() => import('@/features/bus-link/BusLinkPage'));
const FamilyPage = lazy(() => import('@/features/bus-link/FamilyPage'));
const DrivePage = lazy(() => import('@/features/school-bus/drive/DrivePage'));
const WallPage = lazy(() => import('@/features/wall/WallPage'));
const PublicWallPage = lazy(() => import('@/features/wall-public/PublicWallPage'));
const WallsPage = lazy(() => import('@/features/walls/WallsPage'));
const WallBuilderPage = lazy(() => import('@/features/walls/WallBuilderPage'));
const VerifySealPage = lazy(() => import('@/features/verify/VerifySealPage'));
const ColdChainReport = lazy(() => import('@/features/cold-chain/ColdChainReport'));
const ColdChainPage = lazy(() => import('@/features/cold-chain/ColdChainPage'));
const FuelPage = lazy(() => import('@/features/fuel/FuelPage'));
const SchoolBusPage = lazy(() => import('@/features/school-bus/SchoolBusPage'));
const VehiclesPage = lazy(() => import('@/features/vehicles/VehiclesPage'));
const GeofencesPage = lazy(() => import('@/features/geofences/GeofencesPage'));
const POIsPage = lazy(() => import('@/features/pois/POIsPage'));
const AlertsPage = lazy(() => import('@/features/alerts/AlertsPage'));
const MaintenanceDashboard = lazy(() => import('@/features/maintenance/MaintenanceDashboard'));
const DriverBehaviorDashboard = lazy(() => import('@/features/drivers/DriverBehaviorDashboard'));
const DriverBehaviorReport = lazy(() => import('@/features/drivers/DriverBehaviorReport'));
const AlertReport = lazy(() => import('@/features/alerts/AlertReport'));
const CarbonReport = lazy(() => import('@/features/carbon/CarbonReport'));
const DrivingHoursReport = lazy(() => import('@/features/reports/driving-hours/DrivingHoursReport'));

// Reports
const PeopleReport = lazy(() => import('@/features/reports/PeopleReport'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const TripsReport = lazy(() => import('@/features/reports/TripsReport'));
const StopsReport = lazy(() => import('@/features/reports/StopsReport'));
const IdlingReport = lazy(() => import('@/features/reports/IdlingReport'));
const MaintenanceDueReport = lazy(() => import('@/features/reports/MaintenanceDueReport'));
const SummaryReport = lazy(() => import('@/features/reports/SummaryReport'));
const FuelReport = lazy(() => import('@/features/fuel/FuelReport'));
const NonVisitedGeofencesReport = lazy(() => import('@/features/reports/NonVisitedGeofencesReport'));
const VisitsReport = lazy(() => import('@/features/reports/VisitsReport'));
const TripsBetweenGeofencesReport = lazy(() => import('@/features/reports/TripsBetweenGeofencesReport'));
const CustomReportBuilder = lazy(() => import('@/features/reports/CustomReportBuilder'));
const FormsReport = lazy(() => import('@/features/reports/FormsReport'));

// Settings
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const AccountSettings = lazy(() => import('@/features/settings/AccountSettings'));
const ServerSettings = lazy(() => import('@/features/settings/ServerSettings'));
const DriversSettings = lazy(() => import('@/features/settings/DriversSettings'));
const DriversTab = lazy(() => import('@/features/drivers/DriversTab'));
const DriversScoringPage = lazy(() => import('@/features/drivers/DriversScoringPage'));
const DriversHosPage = lazy(() => import('@/features/drivers/DriversHosPage'));
const GroupsSettings = lazy(() => import('@/features/settings/GroupsSettings'));
const CustomFieldsSettings = lazy(() => import('@/features/settings/CustomFieldsSettings'));
const MaintenanceSettings = lazy(() => import('@/features/settings/MaintenanceSettings'));
const CalendarsSettings = lazy(() => import('@/features/settings/CalendarsSettings'));
const CommandsSettings = lazy(() => import('@/features/settings/CommandsSettings'));
const ComputedAttributesSettings = lazy(() => import('@/features/settings/ComputedAttributesSettings'));
const ReplaySettings = lazy(() => import('@/features/settings/ReplaySettings'));
const POIsSettings = lazy(() => import('@/features/settings/POIsSettings'));
const GlobalPOIsTab = lazy(() => import('@/features/settings/GlobalPOIsTab'));
const OrganizationSettings = lazy(() => import('@/features/org/OrganizationSettings'));
const OrganizationTab = lazy(() => import('@/features/org/OrganizationTab'));
const AiCredentialsSettings = lazy(() => import('@/features/org/AiCredentialsSettings'));
const SchoolBusSettings = lazy(() => import('@/features/bus-link/SchoolBusSettings'));
const FeaturesSettings = lazy(() => import('@/features/admin/FeaturesSettings'));
const AdvancedSettings = lazy(() => import('@/features/settings/AdvancedSettings'));
const SensorsTab = lazy(() => import('@/features/settings/SensorsTab'));
const PoisTab = lazy(() => import('@/features/settings/PoisTab'));
const MembersSettings = lazy(() => import('@/features/settings/MembersSettings'));
const AppProfilesSettings = lazy(() => import('@/features/settings/AppProfilesSettings'));
const ScreensSettings = lazy(() => import('@/features/settings/ScreensSettings'));
const ConnectivitySettings = lazy(() => import('@/features/settings/ConnectivitySettings'));
const SensorHealthSettings = lazy(() => import('@/features/settings/SensorHealthSettings'));
const SensorInventoryPage = lazy(() => import('@/features/settings/SensorInventoryPage'));
const ActivityLogPage = lazy(() => import('@/features/settings/ActivityLogPage'));
const SultanExpiryPage = lazy(() => import('@/features/sultan/SultanExpiryPage'));
const ImportsPage = lazy(() => import('@/features/sultan/imports/ImportsPage'));
const ExpirySettings = lazy(() => import('@/features/settings/ExpirySettings'));
const WorkBoard = lazy(() => import('@/features/work/WorkBoard'));
const ScheduledReportsPage = lazy(() => import('@/features/scheduled-reports/ScheduledReportsPage'));
const AnalyticsDashboard = lazy(() => import('@/features/analytics/AnalyticsDashboard'));
const SultanPanel = lazy(() => import('@/features/sultan/SultanPanel'));
const ResellerPanel = lazy(() => import('@/features/reseller/ResellerPanel'));
const IntegrationsSettings = lazy(() => import('@/features/sultan/IntegrationsSettings'));
const AiPage = lazy(() => import('@/features/sultan/AiPage'));
const PlatformBranding = lazy(() => import('@/features/sultan/PlatformBranding'));
const TerminalPage = lazy(() => import('@/features/terminal/TerminalPage'));
const ApiTokensPanel = lazy(() => import('@/features/sultan/ApiTokensPanel'));
const ServersPanel = lazy(() => import('@/features/sultan/ServersPanel'));
const ResellersPanel = lazy(() => import('@/features/sultan/ResellersPanel'));
const BackupsPanel = lazy(() => import('@/features/sultan/BackupsPanel'));
const ModulesPage = lazy(() => import('@/features/sultan/ModulesPage'));
const ChatPage = lazy(() => import('@/features/chat/ChatPage'));
const BroadcastPage = lazy(() => import('@/features/broadcast/BroadcastPage'));
const SafetyDashboard = lazy(() => import('@/features/safety/SafetyDashboard'));
const MusterPage = lazy(() => import('@/features/muster/MusterPage'));
const IncidentsPage = lazy(() => import('@/features/incidents/IncidentsPage'));
const IncidentDetail = lazy(() => import('@/features/incidents/IncidentDetail'));
const EscalationPolicyEditor = lazy(() => import('@/features/incidents/EscalationPolicyEditor'));
const AssurePage = lazy(() => import('@/features/assure/AssurePage'));
const AutomationPage = lazy(() => import('@/features/automation/AutomationPage'));
const IntelligencePage = lazy(() => import('@/features/intelligence/IntelligencePage'));
const FormsPage = lazy(() => import('@/features/forms/FormsPage'));
const FormBuilder = lazy(() => import('@/features/forms/FormBuilder'));
const WorkTemplatesPage = lazy(() => import('@/features/work-templates/WorkTemplatesPage'));
const RiskmizeSettings = lazy(() => import('@/features/riskmize/RiskmizeSettings'));
const AttendancePage = lazy(() => import('@/features/staff/AttendancePage'));
const PeoplePage = lazy(() => import('@/features/people/PeoplePage'));
const TeamPanel = lazy(() => import('@/features/sultan/TeamPanel'));

/** Protect routes that require auth */
function ProtectedRoute({ children }) {
  const user = useSelector((state) => state.session.user);
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Protect routes that require admin */
function AdminRoute({ children }) {
  const user = useSelector((state) => state.session.user);
  const orgRole = useSelector((state) => state.org?.role);
  const isSultan = useSelector((state) => state.sultan?.isSultan);
  if (!user?.administrator && orgRole !== 'admin' && !isSultan) return <Navigate to="/" replace />;
  return children;
}

/** Protect routes that require org admin OR manager (the floor Broadcast's
 *  GET/POST /api/broadcasts routes enforce server-side — see
 *  server/src/broadcast/broadcast.routes.ts). */
function AdminOrManagerRoute({ children }) {
  const user = useSelector((state) => state.session.user);
  const orgRole = useSelector((state) => state.org?.role);
  const isSultan = useSelector((state) => state.sultan?.isSultan);
  if (!user?.administrator && orgRole !== 'admin' && orgRole !== 'manager' && !isSultan) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/** Protect instance-team routes. cap: undefined = any instance staff; 'admin' = Instance Admin only; else the support capability needed. */
function SultanRoute({ children, cap }) {
  const { isAdmin, isStaff, can } = useInstanceAccess();
  const allowed = cap === 'admin' ? isAdmin : cap ? can(cap) : isStaff;
  if (!allowed) return <Navigate to="/" replace />;
  return children;
}

/** Old Customer Modules URL — kept for bookmarks; carries `?org=` across. */
function CustomerModulesRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'organizations');
  return <Navigate to={`/sultan/modules?${params.toString()}`} replace />;
}

/** Protect routes that require a reseller-admin session */
function ResellerRoute({ children }) {
  const reseller = useSelector((state) => state.session.reseller);
  if (!reseller) return <Navigate to="/" replace />;
  return children;
}

function Lazy({ children }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

/** Redirect to dashboard if a feature module is disabled */
// Reports → Cold Chain: the module, or (module off) seals still on record — the same hook as the tab in ReportsPage.
function ColdChainReportGate({ children }) {
  const access = useColdChainReportAccess();
  if (access.pending) return null;
  if (!access.visible) return <Navigate to="/" replace />;
  return children;
}

/** `feature` is one module key or an array that must ALL be on (e.g. a tier on top of its base module). */
function FeatureGate({ feature, children }) {
  const features = useFeatures();
  if (![].concat(feature).every((k) => features[k])) return <Navigate to="/" replace />;
  return children;
}

export default function Router() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<Lazy><SharedTrackingPage /></Lazy>} />
      <Route path="/bus/:token" element={<Lazy><BusLinkPage /></Lazy>} />
      <Route path="/family/:token" element={<Lazy><FamilyPage /></Lazy>} />
      <Route path="/drive/:token" element={<Lazy><DrivePage /></Lazy>} />
      <Route path="/wall" element={<Lazy><WallPage /></Lazy>} />
      <Route path="/w/:token" element={<Lazy><PublicWallPage /></Lazy>} />
      <Route path="/verify/:code?" element={<Lazy><VerifySealPage /></Lazy>} />

      {/* Protected — inside AppLayout shell */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Lazy><DashboardPage /></Lazy>} />
        <Route path="map" element={<LiveMapPage />} />
        <Route path="help" element={<Lazy><HelpPage /></Lazy>} />
        <Route path="support" element={<Lazy><SupportPage /></Lazy>} />

        {/* Reports */}
        <Route path="reports" element={<FeatureGate feature="reports"><Lazy><ReportsPage /></Lazy></FeatureGate>}>
          <Route index element={<Navigate to="trips" replace />} />
          <Route path="people" element={<FeatureGate feature="workforce"><Lazy><PeopleReport /></Lazy></FeatureGate>} />
          <Route path="forms" element={<FeatureGate feature="forms"><Lazy><FormsReport /></Lazy></FeatureGate>} />
          <Route path="cold-chain" element={<ColdChainReportGate><Lazy><ColdChainReport /></Lazy></ColdChainReportGate>} />
          <Route path="trips" element={<Lazy><TripsReport /></Lazy>} />
          <Route path="stops" element={<Lazy><StopsReport /></Lazy>} />
          <Route path="idling" element={<Lazy><IdlingReport /></Lazy>} />
          <Route path="maintenance-due" element={<Lazy><MaintenanceDueReport /></Lazy>} />
          <Route path="summary" element={<Lazy><SummaryReport /></Lazy>} />
          <Route path="fuel" element={<Lazy><FuelReport /></Lazy>} />
          <Route path="driver-behavior" element={<Lazy><DriverBehaviorReport /></Lazy>} />
          <Route path="alerts" element={<Lazy><AlertReport /></Lazy>} />
          <Route path="visits" element={<Lazy><VisitsReport /></Lazy>} />
          <Route path="non-visited" element={<Lazy><NonVisitedGeofencesReport /></Lazy>} />
          <Route path="carbon" element={<Lazy><CarbonReport /></Lazy>} />
          <Route path="driving-hours" element={<Lazy><DrivingHoursReport /></Lazy>} />
          <Route path="trips-between-geofences" element={<Lazy><TripsBetweenGeofencesReport /></Lazy>} />
          <Route path="custom" element={<Lazy><CustomReportBuilder /></Lazy>} />
          <Route path="schedules" element={<FeatureGate feature="scheduledReports" supabase><Lazy><ScheduledReportsPage /></Lazy></FeatureGate>} />
        </Route>

        {/* Alerts */}
        <Route path="alerts" element={<FeatureGate feature="alerts"><Lazy><AlertsPage /></Lazy></FeatureGate>} />

        {/* Cold Chain (TASK-382). /temperature was the coming-soon nav target — keep old links working. */}
        <Route path="cold-chain" element={<FeatureGate feature="temperature"><Lazy><ColdChainPage /></Lazy></FeatureGate>} />
        <Route path="fuel" element={<FeatureGate feature="fuel"><Lazy><FuelPage /></Lazy></FeatureGate>} />
        {/* School Bus Standard (TASK-479): students and family links. */}
        <Route path="school-bus" element={<FeatureGate feature={['schoolBus', 'schoolBusStandard']}><Lazy><SchoolBusPage /></Lazy></FeatureGate>} />
        <Route path="temperature" element={<Navigate to="/cold-chain" replace />} />

        {/* Maintenance Dashboard */}
        <Route path="maintenance" element={<FeatureGate feature="maintenance"><Lazy><MaintenanceDashboard /></Lazy></FeatureGate>} />

        {/* Driver Behavior Dashboard */}
        <Route path="drivers" element={<FeatureGate feature="driverSafety"><Lazy><DriverBehaviorDashboard /></Lazy></FeatureGate>} />

        {/* Geofences */}
        <Route path="geofences" element={<FeatureGate feature="geofences"><Lazy><GeofencesPage /></Lazy></FeatureGate>} />

        {/* POIs */}
        <Route path="pois" element={<FeatureGate feature="pois"><Lazy><POIsPage /></Lazy></FeatureGate>} />

        {/* Administration redirect — merged into Settings → Organization → Features sub-tab */}
        <Route path="admin" element={<Navigate to="/settings/org/features" replace />} />

        {/* Analytics (sultan-only) */}
        <Route path="analytics" element={<SultanRoute><Lazy><AnalyticsDashboard /></Lazy></SultanRoute>} />

        {/* Reseller-admin overview (reseller-admin session, no active org) */}
        <Route path="reseller" element={<ResellerRoute><Lazy><ResellerPanel /></Lazy></ResellerRoute>} />

        {/* Sultan Panel (instance-staff, per-route cap — see SultanRoute) */}
        <Route path="sultan" element={<SultanRoute><Lazy><SultanPanel /></Lazy></SultanRoute>} />
        <Route path="sultan/integrations" element={<SultanRoute cap="admin"><Lazy><IntegrationsSettings /></Lazy></SultanRoute>} />
        {/* One AI page, tabbed (Owner AI / Owner AI Settings / Help Assistant). */}
        <Route path="sultan/ai" element={<SultanRoute cap="admin"><Lazy><AiPage /></Lazy></SultanRoute>} />
        <Route path="sultan/help-assistant" element={<Navigate to="/sultan/ai?tab=help-assistant" replace />} />
        <Route path="sultan/owner-ai" element={<Navigate to="/sultan/ai?tab=owner-ai" replace />} />
        <Route path="sultan/owner-ai-settings" element={<Navigate to="/sultan/ai?tab=owner-ai-settings" replace />} />
        <Route path="sultan/branding" element={<SultanRoute cap="admin"><Lazy><PlatformBranding /></Lazy></SultanRoute>} />
        <Route path="sultan/terminal" element={<SultanRoute><Lazy><TerminalPage /></Lazy></SultanRoute>} />
        <Route path="sultan/api-tokens" element={<SultanRoute cap="admin"><Lazy><ApiTokensPanel /></Lazy></SultanRoute>} />
        <Route path="sultan/servers" element={<SultanRoute cap="admin"><Lazy><ServersPanel /></Lazy></SultanRoute>} />
        <Route path="sultan/resellers" element={<SultanRoute cap="resellers"><Lazy><ResellersPanel /></Lazy></SultanRoute>} />
        <Route path="sultan/backups" element={<SultanRoute cap="admin"><Lazy><BackupsPanel /></Lazy></SultanRoute>} />
        {/* One Modules page, tabbed: Modules + Templates are admin-only, Organizations
            needs the `modules` capability (ModulesPage filters its tabs). */}
        <Route path="sultan/modules" element={<SultanRoute cap="modules"><Lazy><ModulesPage /></Lazy></SultanRoute>} />
        <Route path="sultan/customer-modules" element={<CustomerModulesRedirect />} />
        <Route path="sultan/team" element={<SultanRoute cap="admin"><Lazy><TeamPanel /></Lazy></SultanRoute>} />
        <Route path="sultan/connectivity" element={<SultanRoute><Lazy><ConnectivitySettings mode="sultan" /></Lazy></SultanRoute>} />
        <Route path="sultan/sensor-health" element={<SultanRoute><Lazy><SensorHealthSettings mode="sultan" /></Lazy></SultanRoute>} />
        <Route path="sultan/imports" element={<SultanRoute><Lazy><ImportsPage /></Lazy></SultanRoute>} />
        <Route path="sultan/expiry" element={<SultanRoute><Lazy><SultanExpiryPage /></Lazy></SultanRoute>} />

        {/* Walls (Display Wall builder) */}
        <Route path="walls" element={<Lazy><WallsPage /></Lazy>} />
        <Route path="walls/:id" element={<Lazy><WallBuilderPage /></Lazy>} />

        {/* Settings */}
        <Route path="settings" element={<Lazy><SettingsPage /></Lazy>}>
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={<Lazy><AccountSettings /></Lazy>} />
          <Route path="members" element={<Lazy><MembersSettings /></Lazy>} />
          <Route path="app-profiles" element={<Lazy><AppProfilesSettings /></Lazy>} />
          <Route path="screens" element={<Lazy><ScreensSettings /></Lazy>} />
          <Route path="connectivity" element={<Lazy><ConnectivitySettings mode="admin" /></Lazy>} />
          <Route path="sensors" element={<Lazy><SensorsTab /></Lazy>}>
            <Route index element={<Navigate to="health" replace />} />
            <Route path="health" element={<Lazy><SensorHealthSettings mode="admin" /></Lazy>} />
            <Route path="inventory" element={<Lazy><SensorInventoryPage /></Lazy>} />
          </Route>
          <Route path="activity" element={<Lazy><ActivityLogPage /></Lazy>} />
          <Route path="drivers" element={<Lazy><DriversTab /></Lazy>}>
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<Lazy><DriversSettings /></Lazy>} />
            <Route path="scoring" element={<Lazy><DriversScoringPage /></Lazy>} />
            <Route path="hos" element={<Lazy><DriversHosPage /></Lazy>} />
          </Route>
          <Route path="groups" element={<Lazy><GroupsSettings /></Lazy>} />
          <Route path="maintenance" element={<Lazy><MaintenanceSettings /></Lazy>} />
          <Route path="commands" element={<Lazy><CommandsSettings /></Lazy>} />
          {/* Escalation policies (Respond) — admin-ONLY, not manager, matching
              server/src/incidents/escalation-policies.routes.ts's stricter
              floor: this is config for an unattended spend loop (spec risk 4),
              not day-to-day incident triage, so AdminRoute (not
              AdminOrManagerRoute, which every other Respond surface uses). */}
          <Route
            path="escalation-policies"
            element={
              <FeatureGate feature="respond">
                <AdminRoute>
                  <Lazy><EscalationPolicyEditor /></Lazy>
                </AdminRoute>
              </FeatureGate>
            }
          />
          {/* Automation (webhook endpoints + delivery log) — admin-ONLY,
              matching server/src/automation/automation.routes.ts's
              requireStrictAdmin: a webhook aims an HTTP request from inside
              the server's network, so handing that to the manager role would
              be an SSRF privilege escalation (same reasoning as the
              escalation-policies gate just above). */}
          <Route
            path="automation"
            element={
              <FeatureGate feature="automation">
                <AdminRoute>
                  <Lazy><AutomationPage /></Lazy>
                </AdminRoute>
              </FeatureGate>
            }
          />
          <Route path="replay" element={<Lazy><ReplaySettings /></Lazy>} />
          <Route path="advanced" element={<Lazy><AdvancedSettings /></Lazy>}>
            <Route index element={<Navigate to="server" replace />} />
            <Route path="server" element={<Lazy><ServerSettings /></Lazy>} />
            <Route path="calendars" element={<Lazy><CalendarsSettings /></Lazy>} />
            <Route path="computed-attributes" element={<Lazy><ComputedAttributesSettings /></Lazy>} />
          </Route>
          <Route path="pois" element={<Lazy><PoisTab /></Lazy>}>
            <Route index element={<Navigate to="org" replace />} />
            <Route path="org" element={<Lazy><POIsSettings /></Lazy>} />
            <Route path="global" element={<Lazy><GlobalPOIsTab /></Lazy>} />
          </Route>
          <Route path="org" element={<Lazy><OrganizationTab /></Lazy>}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<Lazy><OrganizationSettings /></Lazy>} />
            <Route path="custom-fields" element={<Lazy><CustomFieldsSettings /></Lazy>} />
            <Route path="features" element={<Lazy><FeaturesSettings /></Lazy>} />
            <Route path="expiry" element={<Lazy><ExpirySettings /></Lazy>} />
            {/* AI provider credentials — strict org-admin only server-side
                (server/src/ai/ai-credentials.routes.ts), same floor as
                Automation/Escalation Policies above, so AdminRoute (not
                AdminOrManagerRoute) mirrors it here too. */}
            <Route
              path="ai"
              element={
                <AdminRoute>
                  <Lazy><AiCredentialsSettings /></Lazy>
                </AdminRoute>
              }
            />
            <Route
              path="school-bus"
              element={
                <AdminRoute>
                  <Lazy><SchoolBusSettings /></Lazy>
                </AdminRoute>
              }
            />
          </Route>
          <Route path="modules/riskmize" element={<Lazy><RiskmizeSettings /></Lazy>} />
          {/* Legacy path redirects — deep-links from emails / external docs */}
          <Route path="custom-fields" element={<Navigate to="/settings/org/custom-fields" replace />} />
          <Route path="features" element={<Navigate to="/settings/org/features" replace />} />
          <Route path="expiry" element={<Navigate to="/settings/org/expiry" replace />} />
          <Route path="sensor-health" element={<Navigate to="/settings/sensors/health" replace />} />
          <Route path="sensor-inventory" element={<Navigate to="/settings/sensors/inventory" replace />} />
          <Route path="global-pois" element={<Navigate to="/settings/pois/global" replace />} />
        </Route>

        {/* Vehicles */}
        <Route path="vehicles" element={<Lazy><VehiclesPage /></Lazy>} />

        {/* Route Replay */}
        <Route path="replay" element={<FeatureGate feature="replay"><Lazy><ReplayPage /></Lazy></FeatureGate>} />

        {/* Work Board (unified Jobs/Tasks/Routes/Forms) */}
        <Route path="work" element={<FeatureGate feature="work"><Lazy><WorkBoard /></Lazy></FeatureGate>} />

        {/* Staff Attendance (relocated from /dispatch/attendance in Plan 3g) */}
        <Route path="attendance" element={<FeatureGate feature="workforce"><Lazy><AttendancePage /></Lazy></FeatureGate>} />

        {/* People — staff roster + last position + shift/SOS state (Phase 3) */}
        <Route path="people" element={<FeatureGate feature="workforce"><Lazy><PeoplePage /></Lazy></FeatureGate>} />

        {/* Chat */}
        <Route
          path="chat"
          element={
            <FeatureGate feature="chat" supabase>
              <Lazy><ChatPage /></Lazy>
            </FeatureGate>
          }
        />

        {/* Broadcast — GET/POST /api/broadcasts are admin/manager-only
            server-side, so the route gate matches (a viewer must not be
            offered a page that will 403). */}
        <Route
          path="broadcast"
          element={
            <FeatureGate feature="broadcast">
              <AdminOrManagerRoute>
                <Lazy><BroadcastPage /></Lazy>
              </AdminOrManagerRoute>
            </FeatureGate>
          }
        />

        {/* Safety Dashboard */}
        <Route
          path="safety"
          element={
            <FeatureGate feature="guardian">
              <Lazy><SafetyDashboard /></Lazy>
            </FeatureGate>
          }
        />

        {/* Muster — the board (declare/mark/close/export) is admin/manager
            only server-side (requireOrgAdmin on every /api/musters route
            except the worker-facing /mine and /respond, which this page
            doesn't use), so AdminOrManagerRoute mirrors that floor exactly
            like Broadcast's own route gate above. */}
        <Route
          path="muster"
          element={
            <FeatureGate feature="muster">
              <AdminOrManagerRoute>
                <Lazy><MusterPage /></Lazy>
              </AdminOrManagerRoute>
            </FeatureGate>
          }
        />

        {/* Respond — the incident list is admin/manager only server-side
            (every /api/incidents route except the worker-facing /mine,
            which this page doesn't use), so AdminOrManagerRoute mirrors
            that floor exactly like Broadcast's and Muster's own route
            gates above. */}
        <Route
          path="incidents"
          element={
            <FeatureGate feature="respond">
              <AdminOrManagerRoute>
                <Lazy><IncidentsPage /></Lazy>
              </AdminOrManagerRoute>
            </FeatureGate>
          }
        />
        {/* Assure — a duty-of-care pack is one person's movement history,
            welfare record and communication log in a single document, so it
            carries the same admin/manager floor the server enforces on
            /api/assure/* (requireOrgAdmin). The nav entry in AppLayout.jsx
            gates on the SAME feature key — they must move together or the
            feature ships invisible with nothing erroring. */}
        <Route
          path="assure"
          element={
            <FeatureGate feature="assure">
              <AdminOrManagerRoute>
                <Lazy><AssurePage /></Lazy>
              </AdminOrManagerRoute>
            </FeatureGate>
          }
        />
        {/* Incident detail (Task 17) — same gate as the list: every
            /api/incidents/:id route (get/patch/acknowledge/resolve/close/
            events/notifications/export) is admin/manager-only server-side. */}
        <Route
          path="incidents/:id"
          element={
            <FeatureGate feature="respond">
              <AdminOrManagerRoute>
                <Lazy><IncidentDetail /></Lazy>
              </AdminOrManagerRoute>
            </FeatureGate>
          }
        />

        {/* Forms (renamed from Inspections per Plan 3e) */}
        <Route path="forms" element={<FeatureGate feature="forms"><Lazy><FormsPage /></Lazy></FeatureGate>} />
        <Route path="forms/builder" element={<FeatureGate feature="forms"><Lazy><FormBuilder /></Lazy></FeatureGate>} />
        <Route path="forms/builder/:id" element={<FeatureGate feature="forms"><Lazy><FormBuilder /></Lazy></FeatureGate>} />

        {/* Work Templates (Plan 3f admin surface) */}
        <Route path="work-templates" element={<FeatureGate feature="workTemplates"><Lazy><WorkTemplatesPage /></Lazy></FeatureGate>} />
        {/* /new and /:id render the same list page with the editor slide-over open. */}
        <Route path="work-templates/new" element={<FeatureGate feature="workTemplates"><Lazy><WorkTemplatesPage creating /></Lazy></FeatureGate>} />
        <Route path="work-templates/:id" element={<FeatureGate feature="workTemplates"><Lazy><WorkTemplatesPage /></Lazy></FeatureGate>} />
        {/* Intelligence */}
        <Route path="intelligence" element={<FeatureGate feature="ai" supabase><Lazy><IntelligencePage /></Lazy></FeatureGate>} />

        {/* Scheduled Reports */}
        {/* Scheduled Reports redirect — merged into Reports → Schedules tab */}
        <Route path="scheduled-reports" element={<Navigate to="/reports/schedules" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
